import { NavLink, useLocation, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSite } from "@/contexts/SiteContext";
import { Home, Building2, Wrench, MapPin, ImageIcon, Mail, Compass, X, Phone } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Sidebar({ open, onClose }) {
  const { t } = useTranslation();
  const { company, contact } = useSite();
  const location = useLocation();

  // Close mobile sidebar on route change
  useEffect(() => { onClose?.(); }, [location.pathname]); // eslint-disable-line

  const links = [
    { to: "/", label: t("nav.home"), Icon: Home },
    { to: "/societe", label: t("nav.about"), Icon: Compass },
    { to: "/prestations", label: t("nav.services"), Icon: Wrench },
    { to: "/projets", label: t("nav.projects"), Icon: Building2 },
    { to: "/disponibilites", label: t("nav.availabilities"), Icon: MapPin },
    { to: "/galerie", label: t("nav.gallery"), Icon: ImageIcon },
    { to: "/contact", label: t("nav.contact"), Icon: Mail },
  ];

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-black/60 z-[55] lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        className={`fixed left-0 top-0 h-screen w-72 bg-atelier-deep border-r atelier-border z-[60] flex flex-col p-6 transition-transform duration-500 ${
          open ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
        style={{ transitionTimingFunction: "cubic-bezier(0.16,1,0.3,1)" }}
        data-testid="sidebar"
      >
        {/* Header / Logo */}
        <div className="flex items-start justify-between mb-10">
          <Link to="/" className="flex items-center gap-3 group" data-testid="sidebar-logo">
            {company?.logo_url ? (
              <img src={company.logo_url} alt="AMAR SAHRAOUI" className="h-10 w-auto" style={{ mixBlendMode: "screen" }} />
            ) : null}
            <div className="leading-none">
              <div className="font-serif text-base tracking-tight text-atelier-text">AMAR SAHRAOUI</div>
              <div className="label-tech text-[0.55rem] text-atelier-textDim mt-1">Promotion Immobilière</div>
            </div>
          </Link>
          <button
            onClick={onClose}
            className="lg:hidden text-atelier-ink p-1"
            data-testid="sidebar-close"
            aria-label="Fermer le menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="h-px w-12 bg-atelier-ink mb-8" />

        {/* Navigation */}
        <nav className="flex flex-col gap-1 flex-grow">
          {links.map(({ to, label, Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={`sidebar-link-${to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-3 label-tech text-[0.72rem] transition-all duration-300 ${
                  isActive
                    ? "text-atelier-ink border-l-2 border-atelier-ink bg-atelier-ink/5"
                    : "text-atelier-textDim hover:text-atelier-text hover:bg-atelier-ink/5 border-l-2 border-transparent"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon className={`w-4 h-4 transition-colors ${isActive ? "text-atelier-ink" : "text-atelier-textFaint group-hover:text-atelier-ink"}`} strokeWidth={1.4} />
                  <span>{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Bottom: language + phone */}
        <div className="mt-8 pt-6 border-t atelier-border-soft space-y-4">
          <LanguageSwitcher />
          {contact?.phone && (
            <a
              href={`tel:${contact.phone.replace(/\s/g, "")}`}
              className="flex items-center gap-3 text-atelier-textDim hover:text-atelier-ink transition-colors group"
              data-testid="sidebar-phone"
            >
              <Phone className="w-3.5 h-3.5 text-atelier-ink" strokeWidth={1.4} />
              <span className="label-tech text-[0.66rem] tracking-widest">{contact.phone}</span>
            </a>
          )}
          <p className="handwritten opacity-50 text-[0.62rem] leading-relaxed">
            "L'architecture est le jeu savant des volumes assemblés sous la lumière."
          </p>
        </div>
      </aside>
    </>
  );
}
